import {Component, computed, inject} from '@angular/core';

import {PerspectiveFacade} from '@selpia/perspective/data-access';
import {AppConfigFacade} from '@selpia/app-config/data-access';
import {ArrayTools, DataBatchTools} from '@selpia/shared/utils';
import {OptionsFacade} from '@selpia/options/data-access';
import {Perspective, Sentiment} from '@selpia/shared/models';
import {BreakdownChartComponent} from '../breakdown-chart/breakdown-chart.component';
import {DataBatchFacade} from '@selpia/data-batch/data-access';

@Component({
  selector: 'lib-feature-dimension-breakdown',
  imports: [BreakdownChartComponent],
  templateUrl: './feature-dimension-breakdown.component.html',
  styleUrl: './feature-dimension-breakdown.component.css',
  host: {
    '[attr.data-sentiment]': 'focusSentiment()',
  },
})
export class FeatureDimensionBreakdownComponent {
  readonly #perspectiveFacade = inject(PerspectiveFacade);
  readonly #appConfigFacade = inject(AppConfigFacade);
  readonly #optionsFacade = inject(OptionsFacade);
  readonly #dataBatchFacade = inject(DataBatchFacade);

  protected readonly focusMeasure = this.#optionsFacade.focusMeasure;
  protected readonly focusPeriod = this.#optionsFacade.focusPeriod;
  protected readonly growthMeasure = this.#optionsFacade.impactMeasure;

  protected readonly focusSentiment = computed(() => {
    const dataChunk = this.#dataBatchFacade.dataChunks()
      ?.at(0);
    const periodCode = this.focusPeriod();
    const measureCode = this.growthMeasure()?.code;
    const date = this.#perspectiveFacade.date();

    if (!dataChunk || !periodCode || !measureCode || !date) {
      return Sentiment.Neutral;
    }

    const dataPoint = DataBatchTools.getDataPointFor(dataChunk.dataPoints, {measureCode, periodCode, date});

    return dataPoint?.sentiment || Sentiment.Neutral;
  });


  protected readonly breakdownPerspectives = computed(() => {
    const perspective = this.#perspectiveFacade.perspective();
    const dimensions = this.#appConfigFacade.dimensions();
    if (!perspective || !dimensions) {
      return [];
    }

    const breakdownPerspectives: Array<Perspective | undefined> = dimensions.map(dimension => {
      const filterLevelNumber = perspective.filterDimensions.find(
        dimensionItem => dimensionItem.dimension === dimension,
      )?.dimensionLevel?.levelNumber;
      const targetLevelNumber = (filterLevelNumber || 0) + 1;

      const focusDimensionLevel = dimension.levels[targetLevelNumber];
      if (!focusDimensionLevel) {
        return undefined;
      }

      return {
        ...perspective,
        focusDimensionLevel,
        includeTotal: false,
      };
    });

    return ArrayTools.removeFalsy(breakdownPerspectives);
  });
}
